import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ui/Toast.js';
import { queryKeys } from '@/lib/query-keys.js';
import { ApiError, errorMessage } from '@/lib/api-error.js';
import { useAutosave } from './useAutosave.js';
import { useEditLock } from './useEditLock.js';
import { useUnsavedGuard } from './useUnsavedGuard.js';
import type { ContentApi, ContentRecord } from './content-api.js';

export type UseContentFormOptions<TForm> = {
  collection: string;
  contentApi: ContentApi<ContentRecord>;
  /** `new` bo'lsa yangi yozuv. */
  id: string;
  basePath: string;
  /** Bo'sh forma — yangi yozuv uchun. */
  empty: () => TForm;
  /** Serverdan kelgan hujjatni formaga o'giradi. */
  fromRecord: (record: ContentRecord) => TForm;
  /** Formani serverga yuboriladigan ko'rinishga o'giradi. */
  toInput: (form: TForm) => Record<string, unknown>;
};

/**
 * Kontent tahrirlash ekranining butun holati.
 *
 * Yuklash, saqlash, nashr, avtosaqlash, qulf, saqlanmagan o'zgarish
 * qo'riqchisi va «Ko'rish» — hammasi shu yerda. 9 ta bo'lim faqat
 * O'Z MAYDONLARINI chizadi.
 */
export function useContentForm<TForm>({
  collection,
  contentApi,
  id,
  basePath,
  empty,
  fromRecord,
  toInput,
}: UseContentFormOptions<TForm>) {
  const isNew = id === 'new';
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<TForm>(empty);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false);

  const record = useQuery({
    queryKey: queryKeys.content.one(collection, id),
    enabled: !isNew,
    queryFn: () => contentApi.one(id),
  });

  // Hujjat kelganda formani BIR MARTA to'ldiramiz. Har `data` o'zgarishida
  // to'ldirsak, avtosaqlashdan keyingi qayta o'qish xodim yozayotgan
  // matnni eskisiga almashtirib yuborardi.
  const loadedFor = useRef<string | null>(null);
  useEffect(() => {
    if (isNew || !record.data || loadedFor.current === id) return;
    loadedFor.current = id;
    setForm(fromRecord(record.data));
    setDirty(false);
    // `fromRecord` bog'liqlikda yo'q: u har renderda yangi funksiya bo'lib
    // keladi va effektni cheksiz qayta ishga tushirardi.
  }, [record.data, id, isNew]);

  const update = useCallback((next: TForm | ((current: TForm) => TForm)) => {
    setForm(next);
    setDirty(true);
  }, []);

  const lock = useEditLock(contentApi, isNew ? null : id);

  const autosave = useAutosave({
    value: form,
    enabled: !isNew && dirty,
    save: async (value) => {
      await contentApi.saveDraft(id, toInput(value));
    },
  });

  const unsaved = useUnsavedGuard(dirty);

  const save = useMutation({
    mutationFn: async () => {
      const input = toInput(form);
      return isNew ? contentApi.create(input) : contentApi.update(id, input);
    },
    onSuccess: async (saved) => {
      setFieldErrors({});
      setDirty(false);
      autosave.markClean();
      toast.success('Saqlandi');

      // Saqlangandan keyin qoralama keraksiz: u faqat SAQLANMAGAN holatni tiklash
      // uchun turadi. Qoldirilsa, keyingi ochilishda «tiklaysizmi?»
      // deb allaqachon saqlangan matn taklif qilinardi.
      if (!isNew) await contentApi.discardDraft(id).catch(() => undefined);

      await queryClient.invalidateQueries({ queryKey: queryKeys.content.all(collection) });
      if (isNew) navigate(`${basePath}/${saved._id}`, { replace: true });
    },
    onError: (error) => {
      if (error instanceof ApiError) setFieldErrors(error.fieldErrors);
      toast.error(errorMessage(error));
    },
  });

  const publish = useMutation({
    mutationFn: (next: boolean) => contentApi.setPublished(id, next),
    onSuccess: async (saved) => {
      toast.success(saved.isPublished ? 'Nashr qilindi' : 'Nashrdan olindi');
      await queryClient.invalidateQueries({ queryKey: queryKeys.content.all(collection) });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  /**
   * «Ko'rish».
   *
   * Avval qoralama saqlanadi: xodim endigina yozgan matnni ko'rmoqchi,
   * lekin u hali serverda yo'q edi. Shundan keyingina havola ochiladi.
   */
  const preview = useCallback(async () => {
    if (isNew) return;
    try {
      if (dirty) await contentApi.saveDraft(id, toInput(form));
      const link = await contentApi.previewLink(id);
      window.open(link.url, '_blank', 'noopener');
    } catch (error) {
      toast.error(errorMessage(error));
    }
  }, [contentApi, dirty, form, id, isNew, toInput, toast]);

  /** Serverdagi qoralama saqlangan holatdan yangiroqmi. */
  const pendingDraft = useMemo(() => {
    const doc = record.data;
    if (!doc?.draft || !doc.draftAt) return null;
    return new Date(doc.draftAt) > new Date(doc.updatedAt) ? doc.draft : null;
  }, [record.data]);

  const restoreDraft = useCallback(() => {
    if (!pendingDraft || !record.data) return;
    update(fromRecord({ ...record.data, ...pendingDraft } as ContentRecord));
  }, [pendingDraft, record.data, update, fromRecord]);

  const discardDraft = useCallback(async () => {
    await contentApi.discardDraft(id).catch(() => undefined);
    await queryClient.invalidateQueries({ queryKey: queryKeys.content.one(collection, id) });
  }, [contentApi, collection, id, queryClient]);

  return {
    isNew,
    form,
    setForm: update,
    fieldErrors,
    dirty,
    loading: !isNew && record.isLoading,
    notFound: !isNew && record.isError,
    record: record.data ?? null,
    isPublished: record.data?.isPublished ?? false,
    autosave,
    lock,
    unsaved,
    saving: save.isPending,
    publishing: publish.isPending,
    save: () => save.mutate(),
    togglePublish: () => publish.mutate(!(record.data?.isPublished ?? false)),
    preview: () => void preview(),
    pendingDraft,
    restoreDraft,
    discardDraft: () => void discardDraft(),
  };
}
